import { getLocalizedAgentName, getLocalizedAgentDesc, getLocalizedAgentCategory, getLocalizedAgentFeatures, formatPriceWithCurrency, Currency } from '../utils/i18n';
import React, { useState, useEffect } from 'react';
import { 
  Sparkles, ArrowLeft, ArrowRight, ShieldCheck, Globe, 
  Compass, Radio, Cpu, Satellite, Eye, MapPin, Activity, 
  Zap, Radar, Layers
} from 'lucide-react';
import * as Icons from 'lucide-react';
import agentsConfig from '../agentsConfig.json';

interface SpaceCenterHubViewProps {
  onLaunchAgent: (target: string) => void;
  onBack: () => void;
  language: 'uk' | 'en' | 'pl';
  currency?: Currency;
  currentUserEmail?: string;
}

export const SPACE_AGENT_IDS = [
  'solotvyno-geomonitor-orchestrator',
  'insar-subsidence-timeseries-analyst',
  'gnss-sat-fusion-geodesist',
  'karst-sinkhole-optical-morphometrist',
  'subsidence-trough-geomechanical-predictor',
  'mine-infrastructure-structural-auditor',
  'hydro-karst-salt-dissolution-modeler',
  'tisza-transboundary-eco-monitor',
  'sat-image-harvester',
  'sat-thematic-processor',
  'sat-drought-water-stress-auditor',
  'satellite-eco-analyst',
  'agrisat-monitor',
  'urban-sat-analyst',
  'maritime-sat-analyst',
  'disaster-sat-response',
  'geoint-defense-analyst',
  'sar-radar-analyst',
  'space-spectrum-consultant',
  'newspace-investment-analyst',
  'satellite-payload-engineer',
  'geological-sat-explorer',
  'ghg-sat-tracker',
  'war-damage-sat-auditor',
  'ncuvkz-mission-control',
  'ncuvkz-sda-debris-tracker',
  'ncuvkz-space-weather-monitor',
  'ncuvkz-gnss-geodesy-auditor',
  'ncuvkz-spacecraft-testing-engineer',
  'gcsk-seismic-auditor',
  'gcsk-acoustic-infrasound-analyst',
  'gcsk-radiation-chemical-monitor',
  'gcsk-geomagnetic-seismoacoustic-analyst',
  'gcsk-emergency-situation-notifier',
  'knp-gnss-integrity-auditor',
  'knp-rtk-diff-correction-operator',
  'knp-time-frequency-metrologist',
  'knp-antijamming-antispoofing-analyst',
  'knp-defense-navigation-briefing-analyst'
];

// Helper icon renderer
const AgentIcon = ({ name, className }: { name: string; className?: string }) => {
  const IconComponent = (Icons as any)[name];
  if (!IconComponent) return <Satellite className={className} />;
  return <IconComponent className={className} />;
};

export default function SpaceCenterHubView({
  onLaunchAgent,
  onBack,
  language, currency = 'UAH'}: SpaceCenterHubViewProps) {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isEn = language === 'en';
  const [selectedSubTab, setSelectedSubTab] = useState<'all' | 'dzz' | 'solotvyno' | 'ncuvkz' | 'gcsk' | 'knp'>('all');
  const [pricing, setPricing] = useState<Record<string, { price: number; limit: number; durationDays: number; singlePrice: number }>>({});

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await fetch('/api/subscription/pricing');
        if (res.ok) {
          const data = await res.json();
          setPricing(data);
        }
      } catch (err) {
        console.error('Failed to fetch pricing:', err);
      }
    };
    fetchPricing();
  }, []);

  const allSpaceAgents = agentsConfig.filter((a: any) => SPACE_AGENT_IDS.includes(a.id));

  const filteredAgents = allSpaceAgents.filter((agent: any) => {
    if (selectedSubTab === 'all') return true;
    if (selectedSubTab === 'solotvyno') {
      return [
        'solotvyno-geomonitor-orchestrator',
        'insar-subsidence-timeseries-analyst',
        'gnss-sat-fusion-geodesist',
        'karst-sinkhole-optical-morphometrist',
        'subsidence-trough-geomechanical-predictor',
        'mine-infrastructure-structural-auditor',
        'hydro-karst-salt-dissolution-modeler',
        'tisza-transboundary-eco-monitor'
      ].includes(agent.id);
    }
    if (selectedSubTab === 'dzz') {
      return [
        'solotvyno-geomonitor-orchestrator',
        'insar-subsidence-timeseries-analyst',
        'gnss-sat-fusion-geodesist',
        'karst-sinkhole-optical-morphometrist',
        'subsidence-trough-geomechanical-predictor',
        'mine-infrastructure-structural-auditor',
        'hydro-karst-salt-dissolution-modeler',
        'tisza-transboundary-eco-monitor',
        'satellite-eco-analyst', 'agrisat-monitor', 'urban-sat-analyst',
        'maritime-sat-analyst', 'disaster-sat-response', 'geoint-defense-analyst',
        'sar-radar-analyst', 'space-spectrum-consultant', 'newspace-investment-analyst',
        'satellite-payload-engineer', 'geological-sat-explorer', 'ghg-sat-tracker',
        'war-damage-sat-auditor', 'sat-image-harvester', 'sat-thematic-processor',
        'sat-drought-water-stress-auditor'
      ].includes(agent.id);
    }
    if (selectedSubTab === 'ncuvkz') {
      return agent.id.startsWith('ncuvkz-');
    }
    if (selectedSubTab === 'gcsk') {
      return agent.id.startsWith('gcsk-');
    }
    if (selectedSubTab === 'knp') {
      return agent.id.startsWith('knp-');
    }
    return true;
  });

  return (
    <div className="space-y-8 animate-fade-in pb-12 font-mono">
      {/* Header Bar */}
      <div className="flex items-center justify-between gap-4 flex-wrap bg-slate-900 border border-slate-800 rounded-xl p-4 text-white">
        <button
          onClick={onBack}
          className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{isPl ? '← Powrót do Panelu Głównego' : isUk ? '← Назад до Головного Хабу' : '← Back to Main Hub'}</span>
        </button>

        <div className="flex items-center gap-2">
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-blue-500/20 text-blue-300 border border-blue-400/30 rounded-md">
            {isPl ? '🛰️ 38 Modułów Satelitarnych & GEOINT' : isUk ? '🛰️ 38 Аналітичних Модулів ДЗЗ' : '🛰️ 38 Space & GEOINT Modules'}
          </span>
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded-md flex items-center gap-1">
            <Sparkles className="w-3 h-3 text-emerald-400" />
            {isPl ? 'Sentinel • Landsat • SAR InSAR • GNSS' : isUk ? 'Sentinel • Landsat • SAR InSAR • GNSS' : 'Sentinel • Landsat • SAR InSAR • GNSS'}
          </span>
        </div>
      </div>

      {/* Hero Banner */}
      <div className="relative bg-gradient-to-r from-slate-950 via-indigo-950 to-slate-900 border border-indigo-500/40 rounded-2xl p-6 sm:p-8 text-white shadow-2xl overflow-hidden">
        <div className="absolute right-0 top-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-3xl space-y-3">
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-blue-500/30 text-blue-300 border border-blue-400/40 rounded">
              {isPl ? 'OPERACJE KOSMICZNE • GEODYNAMIKA • KONTROLA SEJSMICZNA • POLE NAWIGACYJNE • TELEDETEKCJA' : isUk ? 'КОСМІЧНІ ОПЕРАЦІЇ • ГЕОДИНАМІКА • СПЕЦКОНТРОЛЬ • НАВІГАЦІЙНЕ ПОЛЕ • ДЗЗ' : 'SPACE OPERATIONS • GEODYNAMICS • SPECIAL CONTROL • NAVIGATION • REMOTE SENSING'}
            </span>
          </div>

          <h1 className="text-2xl sm:text-4xl font-extrabold tracking-tight uppercase leading-tight bg-gradient-to-r from-white via-slate-100 to-blue-300 bg-clip-text text-transparent">
            {isPl 
              ? 'CENTRUM KOSMICZNE, NAWIGACJI & TELEDETEKCJI' 
              : isUk 
              ? 'КОСМОС, НАВІГАЦІЯ, СПЕЦКОНТРОЛЬ' 
              : 'SPACE, NAVIGATION & SPECIAL CONTROL'}
          </h1>

          <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-mono">
            {isPl
              ? 'Specjalistyczny kompleks do pobierania i analizy zdjęć satelitarnych (Sentinel/Copernicus), monitoringu geodynamicznego kopalni i osiadań InSAR, kontroli sejsmicznej CTBTO oraz integralności nawigacji GNSS.'
              : isUk
              ? 'Спеціалізований комплекс для супутникового ДЗЗ, геодинамічного моніторингу шахтних деформацій та карсту (InSAR), моніторингу космічного простору (SDA), сейсмоконтролю CTBTO та навігації GNSS.'
              : 'Dedicated space hub featuring AI agents for Earth observation data harvesting, InSAR geodynamics & mine subsidence, Space Domain Awareness (SDA), CTBTO seismic control & GNSS integrity.'}
          </p>
        </div>
      </div>

      {/* Sub-Category Filter Tabs */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
        <button
          onClick={() => setSelectedSubTab('all')}
          className={`px-4 py-2 rounded-xl text-xs font-bold uppercase transition-all whitespace-nowrap cursor-pointer ${
            selectedSubTab === 'all'
              ? 'bg-blue-600 text-white shadow-md shadow-blue-600/30'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          {isPl ? 'Wszyscy Agenci Kosmosu (38)' : isUk ? 'Всі Агенти Космосу (38)' : 'All Space Agents (38)'}
        </button>

        <button
          onClick={() => setSelectedSubTab('solotvyno')}
          className={`px-4 py-2 rounded-xl text-xs font-bold uppercase transition-all whitespace-nowrap cursor-pointer ${
            selectedSubTab === 'solotvyno'
              ? 'bg-amber-600 text-white shadow-md shadow-amber-600/30'
              : 'bg-slate-900 text-amber-300 hover:bg-slate-800 border border-amber-800/50'
          }`}
        >
          {isPl ? '⛏️ Geodynamika & Solotwino (8)' : isUk ? '⛏️ Геодинаміка & Солотвино (8)' : '⛏️ Geodynamics & Mining (8)'}
        </button>

        <button
          onClick={() => setSelectedSubTab('dzz')}
          className={`px-4 py-2 rounded-xl text-xs font-bold uppercase transition-all whitespace-nowrap cursor-pointer ${
            selectedSubTab === 'dzz'
              ? 'bg-blue-600 text-white shadow-md shadow-blue-600/30'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          {isPl ? '🛰️ Teledetekcja & Satelity (23)' : isUk ? '🛰️ Космос & ДЗЗ (23)' : '🛰️ Space & Earth Obs (23)'}
        </button>

        <button
          onClick={() => setSelectedSubTab('ncuvkz')}
          className={`px-4 py-2 rounded-xl text-xs font-bold uppercase transition-all whitespace-nowrap cursor-pointer ${
            selectedSubTab === 'ncuvkz'
              ? 'bg-blue-600 text-white shadow-md shadow-blue-600/30'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          {isPl ? '🛸 Operacje Kosmiczne (5)' : isUk ? '🛸 Космічні Операції (5)' : '🛸 Space Operations (5)'}
        </button>

        <button
          onClick={() => setSelectedSubTab('gcsk')}
          className={`px-4 py-2 rounded-xl text-xs font-bold uppercase transition-all whitespace-nowrap cursor-pointer ${
            selectedSubTab === 'gcsk'
              ? 'bg-blue-600 text-white shadow-md shadow-blue-600/30'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          {isPl ? '🌋 Kontrola Specjalna (5)' : isUk ? '🌋 Спеціальний Контроль (5)' : '🌋 Special Control (5)'}
        </button>

        <button
          onClick={() => setSelectedSubTab('knp')}
          className={`px-4 py-2 rounded-xl text-xs font-bold uppercase transition-all whitespace-nowrap cursor-pointer ${
            selectedSubTab === 'knp'
              ? 'bg-blue-600 text-white shadow-md shadow-blue-600/30'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          {isPl ? '📍 Pole Nawigacyjne GNSS (5)' : isUk ? '📍 Навігаційне Поле (5)' : '📍 Navigation Field (5)'}
        </button>
      </div>

      {/* Grid of Agent Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {filteredAgents.map((agent: any) => {
          const agentPricing = pricing[agent.id];
          const priceVal = agentPricing ? agentPricing.price : 490;
          const limitVal = agentPricing ? agentPricing.limit : 30;
          const durationDaysVal = agentPricing ? agentPricing.durationDays : 30;
          const singlePriceVal = agentPricing ? agentPricing.singlePrice : 25;

          return (
            <div
              key={agent.id}
              className="group relative bg-white border border-slate-200 hover:border-blue-500/40 rounded-xl p-6 flex flex-col justify-between transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/5"
            >
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-lg bg-blue-50 text-blue-600 border border-blue-200/60 group-hover:scale-105 transition-transform">
                    <AgentIcon name={agent.icon} className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[9px] font-extrabold uppercase px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">
                        {getLocalizedAgentCategory(agent, language)}
                      </span>
                      <span className="text-[9px] font-extrabold uppercase px-2 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200">
                        ✨ Gemini 3.7 Flash
                      </span>
                      <span className="text-[9px] font-extrabold uppercase px-2 py-0.5 rounded bg-red-50 text-red-600 border border-red-200 font-mono">
                        {formatPriceWithCurrency(priceVal, currency)} / {limitVal} {isUk ? (limitVal === 1 ? 'запит' : 'запитів') : isPl ? 'zapytań' : 'req.'} ({durationDaysVal} {isUk ? 'дн.' : isPl ? 'dni' : 'days'})
                      </span>
                      {singlePriceVal > 0 && (
                        <span className="text-[9px] font-extrabold uppercase px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 font-mono">
                          {isUk ? `Разово: ${formatPriceWithCurrency(singlePriceVal, currency)}` : isPl ? `Pojedynczo: ${formatPriceWithCurrency(singlePriceVal, currency)}` : `Single: ${formatPriceWithCurrency(singlePriceVal, currency)}`}
                        </span>
                      )}
                    </div>
                    <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide mt-1.5 leading-snug">
                      {getLocalizedAgentName(agent, language)}
                    </h3>
                  </div>
                </div>

                <p className="text-xs text-slate-600 leading-relaxed font-mono">
                  {getLocalizedAgentDesc(agent, language)}
                </p>

                {/* Features list */}
                <ul className="space-y-1.5 pt-1">
                  {getLocalizedAgentFeatures(agent, language).map((feat: string, idx: number) => (
                    <li key={idx} className="flex items-center gap-2 text-[11px] text-slate-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
                      <span className="truncate">{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Launch Action */}
              <div className="pt-6 mt-4 border-t border-slate-100 flex items-center justify-between gap-3">
                <div className="flex items-center gap-1.5 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded border border-emerald-200">
                  <Sparkles className="w-3 h-3 text-emerald-600 animate-pulse" />
                  <span>{isPl ? '🎁 1. start ZA DARMO' : isUk ? '🎁 1-й запуск БЕЗКОШТОВНО' : '🎁 1st Run FREE'}</span>
                </div>

                <button
                  onClick={() => onLaunchAgent(agent.target)}
                  className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-md shadow-blue-600/20 flex items-center gap-2 cursor-pointer hover:scale-[1.02]"
                >
                  <span>{isPl ? '🚀 Uruchom Zadanie' : isUk ? '🚀 Виконати Задачу' : '🚀 Execute Task'}</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
